import { useI18n } from 'next-localization';
import BaseSearch from 'components/Generic/SearchBar/BaseSearch';
import { useSitecoreContext } from 'hooks/useSitecoreContext';
import { useLazyFetchDocumentsQuery } from '@/store/literatureHub/services/literatureHubAPI';
import { useEffect, useMemo, useState } from 'react';
import debounce from 'lodash.debounce';
import { AppliedFilters } from 'model/LiteratureHub';

type Props = {
  appliedFilters: Array<AppliedFilters>;
  itemsPerPage: number;
};

const LiteratureSearch = ({ appliedFilters, itemsPerPage }: Props) => {
  const { t } = useI18n();
  const { sitecoreContext } = useSitecoreContext();
  const cookieName: string = `literatureSearchHistory#${sitecoreContext.site?.name || ''}`;
  const [fetchDocuments, { isFetching }] = useLazyFetchDocumentsQuery();
  const [data, setData] = useState({
    searchMessageText: '',
    results: [],
  });

  const payload = useMemo(() => {
    return {
      skip: 0,
      take: itemsPerPage,
      searchQuery: '',
      filters: appliedFilters.map(({ name, values }) => ({
        name,
        values,
      })),
    };
  }, []);

  const debouncedRequestCall = useMemo(() => {
    return debounce(async (searchInputData: string) => {
      try {
        const { data } = await fetchDocuments({
          ...payload,
          searchQuery: searchInputData,
        });
        if (data) {
          const { value } = data;
          setData(() => ({
            searchMessageText: value.length > 0 ? '' : t('noResultsMessage'),
            results: value,
          }));
        }
      } catch (e) {
        console.error('Search fetching error: ', e);
      }
    }, 300);
  }, [payload, fetchDocuments, t]);

  useEffect(() => {
    return () => {
      debouncedRequestCall.cancel();
    };
  }, [debouncedRequestCall]);

  return (
    <>
      <h3>{t('literatureHubSearchTitle')}</h3>
      <BaseSearch
        cookieName={cookieName}
        placeholder={t('literatureHubSearchDescription')}
        fieldName="literatureSearch"
        allowSearchByISIN={false}
        searchResultsProp={data}
        getSuggestions={debouncedRequestCall}
        isFetching={isFetching}
      />
    </>
  );
};

export default LiteratureSearch;
